import AngleLeft from "@icons/angle-left.svg?react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import {
  Outlet,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import invariant from "tiny-invariant";
import { z } from "zod";

import { traderSchema } from "@/api/types/digitalnisklady.cz/types";
import {
  canSignCheckFn,
  useGuard,
} from "@/common/acl/action-guard/action-guard";
import { Button } from "@/common/components/button/button";
import { trackTradingOrderClick } from "@/common/components/mixpanel/mixpanel-tracking";
import { Fields, OrderForm } from "@/common/components/order-form/order-form";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { SearchParamsLink } from "@/common/components/search-params-link/search-params-link";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { entitySchema } from "@/common/schema/entity";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";

import { FullscreenModalLayout } from "../layout/layout.fullscreen";
import { usePostalCodesQuery } from "../shared/api/get-postal-codes";
import { usePostalCodeDialogParams } from "../shared/components/postal-code-dialog/postal-code-dialog";
import { useGoToFailed } from "../shared/routes/failed/failed";
import { path as successPath } from "../shared/routes/success/success";
import { useAddFastOrderMutation } from "./quick-trading.detail.api";

const path = "order";
const Order = () => {
  const { id: companyId } = useRequiredErpCompany();
  const context = useOrderContext();
  const addFastOrder = useAddFastOrderMutation();
  const [, currency] = useCurrency();
  const navigate = useNavigate();
  const postalCodes = useQuery(usePostalCodesQuery());
  const { params } = usePostalCodeDialogParams();
  const [searchParams] = useSearchParams();
  const postalCodeObject = postalCodes.data?.find(
    (postalCode) => postalCode.code === params?.postalCode,
  );
  const goToFailedScreen = useGoToFailed();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(postalCodeObject, "Postal code is not set");
  const handleSubmit: React.ComponentProps<
    typeof OrderForm<Fields>
  >["onSubmit"] = (data) => {
    trackTradingOrderClick({
      crop: context.crop.name,
      currency,
      price: data.pricePerTon,
      quarter: context.quarter.name,
    });
    addFastOrder.mutate(
      {
        amount: data.tons,
        companyId,
        cropId: context.crop.id,
        currency: currency as Currency,
        gps: {
          latitude: context.postalCodeObject.latitude,
          longitude: context.postalCodeObject.longitude,
        },
        harvestYear: data.harvestYear,
        lowPrice: data.lowPricePerTon,
        note: data.note,
        price: data.pricePerTon,
        quarterId: data.quarter,
        zip: postalCodeObject.code,
        ...(data.trader === "ALL" ? {} : { onlyTrader: data.trader }),
      },
      {
        onError: goToFailedScreen,
        onSuccess: async (data) => {
          await navigate(
            `${data.addFastOrder?.id}/offer/${data.addFastOrder?.offer?.id}/${successPath}?${searchParams.toString()}`,
          );
        },
      },
    );
  };
  const guardedSubmit = useGuard({
    action: handleSubmit,
    checkFn: canSignCheckFn,
  });

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <SearchParamsLink replace to="..">
            <AngleLeft />
          </SearchParamsLink>
        }
      >
        <FullscreenModalLayout.Title>
          <Trans>Nastavit příkaz k prodeji</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[
            {
              label: <Trans>Komodita</Trans>,
              value: context.crop.name,
            },
            {
              label: <Trans>PSČ</Trans>,
              value: context.postalCodeObject.label,
            },
          ]}
        />
        <Spacer className="h-6" />
        <OrderForm<Fields>
          initialValues={{
            pricePerTon: context.pricePerTon,
            quarter: context.quarter.id,
            tons: context.amount,
            trader: context.traderId,
          }}
          onSubmit={guardedSubmit}
        >
          <OrderForm.QuarterField />
          <OrderForm.LowPriceField currency={currency} />
          <OrderForm.PriceField currency={currency} />
          <OrderForm.TraderField>
            <option value="ALL">
              <Trans>Všichni obchodníci</Trans>
            </option>
            {context.traders.map((trader) => (
              <option key={trader.id} value={trader.id}>
                {trader.name}
              </option>
            ))}
          </OrderForm.TraderField>
          <OrderForm.AmountField />
          <OrderForm.NoteField />
        </OrderForm>
        <Button.Container>
          <Button form={OrderForm.formId} type="submit" variant="primary">
            <Trans>Nastavit příkaz</Trans>
          </Button>
        </Button.Container>
      </FullscreenModalLayout>
      <Outlet />
    </>
  );
};

const contextSchema = z.object({
  amount: z.number().or(z.nan()).optional(),
  crop: entitySchema,
  postalCodeObject: z.object({
    code: z.string(),
    label: z.string(),
    latitude: z.number(),
    longitude: z.number(),
    name: z.string(),
  }),
  pricePerTon: z.number(),
  quarter: entitySchema,
  traderId: z.string(),
  traders: z.array(traderSchema),
});

const useOrderContext = () => {
  const context = useOutletContext();

  return contextSchema.parse(context);
};

export { Order, path };
